import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  fetchCompetitionLeaderboard,
  getCurrentMonthScoreField,
} from "../utils/authService";

export default function useCompetitionLeaderboard({
  enabled = false,
  lang = "en",
  modalVisible = false,
} = {}) {
  const [entries, setEntries] = useState([]);
  const [oppositeMonthTop3, setOppositeMonthTop3] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const monthInfo = useMemo(() => {
    const baseInfo = getCurrentMonthScoreField();
    const formatter = new Intl.DateTimeFormat(
      lang === "nl" ? "nl-NL" : "en-US",
      {
        month: "long",
        // year: "numeric",
      }
    );
    return {
      ...baseInfo,
      label: formatter.format(new Date()),
    };
  }, [lang]);

  const formattedMonthLabel =
    typeof monthInfo.label === "string" && monthInfo.label.length > 0
      ? monthInfo.label.charAt(0).toUpperCase() +
        monthInfo.label.slice(1).toLowerCase()
      : monthInfo.label;

  const refresh = useCallback(async () => {
    if (!enabled) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await fetchCompetitionLeaderboard();
      if (!isMountedRef.current) {
        return;
      }
      // Handle both old format (array) and new format (object with entries and oppositeMonthTop3)
      if (Array.isArray(data)) {
        setEntries(data);
        setOppositeMonthTop3([]);
      } else if (data && Array.isArray(data.entries)) {
        setEntries(data.entries);
        setOppositeMonthTop3(Array.isArray(data.oppositeMonthTop3) ? data.oppositeMonthTop3 : []);
      } else {
        setEntries([]);
        setOppositeMonthTop3([]);
      }
    } catch (err) {
      console.warn("Failed to fetch competition leaderboard", err);
      if (isMountedRef.current) {
        setError(
          lang === "nl"
            ? "Kon de competitie niet laden... probeer het later opnieuw."
            : "Could not load the competition standings... try again later."
        );
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, lang]);

  useEffect(() => {
    if (!enabled || !modalVisible) {
      return;
    }
    refresh();
  }, [enabled, modalVisible, refresh]);

  const messageLines = useMemo(() => {
    if (!enabled) {
      return lang === "nl"
        ? ["De maandelijkse competities starten binnenkort!"]
        : ["Monthly competitions start soon!"];
    }
    return [];
  }, [enabled, lang]);

  const texts = useMemo(() => {
    if (!enabled) {
      return {};
    }
    return lang === "nl"
      ? {
          loadingLabel: "Bezig met laden…",
          emptyLabel: "Nog geen scores voor deze maand.",
          retryLabel: "Opnieuw",
        }
      : {
          loadingLabel: "Loading…",
          emptyLabel: "No scores for this month yet.",
          retryLabel: "Retry",
        };
  }, [enabled, lang]);

  const tableLabels = useMemo(
    () =>
      lang === "nl"
        ? { rank: "Nr", player: "Speler", score: "Score" }
        : { rank: "#", player: "Player", score: "Score" },
    [lang]
  );

  return {
    competitionEntries: entries,
    competitionOppositeMonthTop3: oppositeMonthTop3,
    competitionLoading: loading,
    competitionError: error,
    competitionMonthLabel:
      lang === "nl"
        ? `${formattedMonthLabel}competitie`
        : `${formattedMonthLabel} competition`,
    competitionMessageLines: messageLines,
    competitionTexts: texts,
    competitionTableLabels: tableLabels,
    refreshCompetitionLeaderboard: refresh,
  };
}

